import type {
  AssignmentProcess,
  AssignmentProcessStepRecording,
  ProcessEventNode
} from '@bliks-io/contracts';
import type { ProcessStepAssignment } from '@/api/queries/useTasks';
import { isEventNodeSource } from '@/lib/assignment-source';
import type { ProcessStepInfo } from '../types';

export interface ProcessStepResult {
  label: string;
  description?: string;
  actionType?: 'task' | 'decision' | 'start';
  /** BPMN event type of a start-event source — drives the cell icon. */
  eventType?: string;
  ruleConditions?: Array<{ key: string; value: string }>;
  ruleOutput?: string;
}

// Type for the assignment parameter - allow flexible process type
type ProcessStepAssignmentParam =
  | ProcessStepAssignment
  | { process: string | AssignmentProcess | Record<string, unknown> };

/**
 * Legacy 'START'-marker arm: in-flight pre-issue-11 assignments carry no
 * populated source node, so the start-event info is read from the process's
 * `recording.initialEvent` mirror. Kept one release past P6, when migration
 * M2 has drained the marker stock.
 */
function getStartStepInfo(
  assignment: ProcessStepAssignmentParam | undefined,
  t: (key: string) => string
): ProcessStepResult {
  // Try to extract event information from the assignment's process recording
  if (
    assignment &&
    typeof assignment.process === 'object' &&
    'recording' in assignment.process &&
    assignment.process.recording &&
    typeof assignment.process.recording === 'object' &&
    'initialEvent' in assignment.process.recording &&
    assignment.process.recording.initialEvent &&
    typeof assignment.process.recording.initialEvent === 'object' &&
    'name' in assignment.process.recording.initialEvent
  ) {
    const event = assignment.process.recording.initialEvent as {
      name?: string;
      description?: string;
      eventType?: string;
    };
    return {
      label: event.name || t('myTasks.processStart'),
      description: event.description,
      actionType: 'start',
      eventType: event.eventType
    };
  }
  return { label: t('myTasks.processStart'), actionType: 'start' };
}

/**
 * Post-flip arm (issue 11): the assignment source is a populated start event
 * node, so label/description/icon come straight from the node — no mirror
 * lookup needed.
 */
function getEventNodeStepInfo(
  node: ProcessEventNode,
  t: (key: string) => string
): ProcessStepResult {
  return {
    label: node.name || t('myTasks.processStart'),
    description: node.description || undefined,
    actionType: 'start',
    eventType: node.eventType || undefined
  };
}

function getTaskStepInfo(step: ProcessStepInfo): ProcessStepResult {
  return {
    label: step.generatedName || step.task?.taskDescription || '',
    description: step.generatedDescription || step.task?.output,
    actionType: 'task'
  };
}

function getDecisionStepInfo(
  step: ProcessStepInfo,
  ruleIds: string[] | undefined,
  t: (key: string) => string
): ProcessStepResult {
  // Extract the event name for the decision label
  const eventInput = step.inputs?.find((input) => input.inputType === 'event');
  // Derived event inputs no longer persist `name` — fall back to the
  // generic decision-step label when the event hasn't been hydrated.
  const fallbackLabel =
    eventInput && eventInput.inputType === 'event'
      ? eventInput.name || t('myTasks.decisionStep')
      : t('myTasks.decisionStep');
  // Use generatedName as priority, fallback to event name or default
  const decisionLabel = step.generatedName || fallbackLabel;

  // If ruleIds are provided, find and return the matched rules
  const hasRuleIds = ruleIds && ruleIds.length > 0 && step.rules;
  if (!hasRuleIds) {
    return {
      label: decisionLabel,
      description: eventInput?.description,
      actionType: 'decision'
    };
  }

  const matchedRules = step.rules!.filter((rule) => rule._id && ruleIds!.includes(rule._id));
  if (matchedRules.length === 0) {
    return {
      label: decisionLabel,
      description: eventInput?.description,
      actionType: 'decision'
    };
  }

  // For multiple rules, show a summary
  if (matchedRules.length > 1) {
    const ruleOutputs = matchedRules.map((r) => r.output).join(', ');
    return {
      label: decisionLabel,
      description: eventInput?.description,
      actionType: 'decision',
      ruleOutput: ruleOutputs
    };
  }

  // For single rule, show conditions
  const matchedRule = matchedRules[0];
  const ruleConditions = matchedRule.inputs.map((input) => ({
    key: input.column,
    value: input.value
  }));
  return {
    label: decisionLabel,
    description: eventInput?.description,
    actionType: 'decision',
    ruleConditions,
    ruleOutput: matchedRule.output
  };
}

export function getProcessStepInfo(
  step:
    | string
    | ProcessStepInfo
    | AssignmentProcessStepRecording
    | 'START'
    | ProcessStepAssignment['sourceProcessStep'],
  ruleIds: string[] | undefined,
  assignment: ProcessStepAssignmentParam | undefined,
  t: (key: string) => string
): ProcessStepResult {
  if (step === 'START') {
    return getStartStepInfo(assignment, t);
  }

  if (isEventNodeSource(step)) {
    return getEventNodeStepInfo(step, t);
  }

  if (typeof step === 'string') {
    return { label: t('myTasks.processStepGeneric') };
  }

  // It's a populated ProcessStepRecording
  if (step.actionType === 'task' && (step.generatedName || step.task?.taskDescription)) {
    return getTaskStepInfo(step);
  }

  if (step.actionType === 'decision') {
    return getDecisionStepInfo(step, ruleIds, t);
  }

  return { label: t('myTasks.processStepGeneric'), actionType: step.actionType };
}

/** A bare id means the predecessor recording is gone — nothing left to label. */
export function isUnresolvedStepId(
  step: ProcessStepAssignment['sourceProcessStep'] | string
): boolean {
  return typeof step === 'string' && step !== 'START';
}

/** Mirrors what `ProcessStepCell` shows, so search matches the visible text. */
export function getProcessStepSearchText(info: ProcessStepResult): string {
  const parts = info.ruleConditions?.length
    ? [
        info.label,
        ...info.ruleConditions.map(({ key, value }) => `${key} ${value}`),
        info.ruleOutput
      ]
    : [info.label, info.description, info.ruleOutput];
  return parts.filter(Boolean).join(' ');
}
